// frontend/src/pages/ReporteGeves.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import contentService from '../services/contentService';
import './ReporteGeves.css';

import Loading from '../components/Loading';
/* ── helpers ── */
const fmtN = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return Number(n).toLocaleString('es-CL');
};
// Formateador para eje Y del chart: más decimales cuando el rango es pequeño
const fmtAxis = (n, range) => {
  if (n == null) return '—';
  if (n >= 1_000_000) {
    const decimals = range < 100_000 ? 3 : range < 1_000_000 ? 2 : 1;
    return `${(n / 1_000_000).toFixed(decimals)}M`;
  }
  if (n >= 1_000) {
    const decimals = range < 1_000 ? 3 : range < 5_000 ? 2 : 1;
    return `${(n / 1_000).toFixed(decimals)}K`;
  }
  return Number(n).toLocaleString('es-CL');
};
const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}%`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const iso30DaysAgo = () => {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
// Extrae el shortcode de un permalink de Instagram (/p/ABC123/, /reel/ABC123/, /tv/ABC123/)
// Se usa para saber si un post scrapeado en "Mi Marca" es el MISMO post que ya trae META
// (posts propios) o si falta (típicamente collabs donde la cuenta no es la "creadora").
const extractShortcode = (permalink) => {
  if (!permalink) return null;
  const m = String(permalink).match(/\/(?:p|reel|tv)\/([^/?]+)/);
  return m ? m[1] : null;
};

const TYPE_META = {
  VIDEO:          { label: 'Reel',     color: '#e1306c', bg: '#fce4ec' },
  IMAGE:          { label: 'Foto',     color: '#1565c0', bg: '#e3f2fd' },
  CAROUSEL_ALBUM: { label: 'Carrusel', color: '#4527a0', bg: '#ede7f6' },
};

/* ── Badge tipo ── */
function TypeBadge({ mediaType }) {
  const m = TYPE_META[mediaType] || { label: mediaType || '—', color: '#475569', bg: '#f1f5f9' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      background: m.bg, color: m.color, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

/* ── Avatar ── */
function Avatar({ profile, size = 32 }) {
  const [err, setErr] = useState(false);
  const initials = (profile.username || '').slice(0, 2).toUpperCase();
  if (profile.profile_pic_url && !err) {
    return (
      <img src={profile.profile_pic_url} alt="" onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#e0e7ff',
      color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.35, flexShrink: 0,
    }}>{initials}</div>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, sub }) {
  return (
    <div className="rg2-kpi">
      <div className="rg2-kpi-lbl">{label}</div>
      <div className="rg2-kpi-val">{value}</div>
      {sub && <div className="rg2-kpi-sub">{sub}</div>}
    </div>
  );
}

/* ── Tooltip flotante para charts ── */
function ChartTooltip({ tip }) {
  if (!tip) return null;
  // Ajustar para no salirse por la derecha/arriba
  const style = {
    position: 'absolute',
    left: tip.x + 14,
    top: tip.y - 44,
    background: '#002421',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    pointerEvents: 'none',
    zIndex: 200,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,36,33,0.25)',
  };
  return (
    <div style={style}>
      {tip.label && <div style={{ color: '#71d234', fontSize: 10, fontWeight: 700, marginBottom: 2 }}>{tip.label}</div>}
      <div>{tip.value}</div>
    </div>
  );
}

/* ── Mini bar chart SVG ── */
function BarChart({ posts, metric, color, label }) {
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  const data = posts
    .filter(p => p[metric] != null)
    .slice()
    .sort((a, b) => new Date(a.posted_at) - new Date(b.posted_at))
    .slice(-30);

  if (!data.length) return <div className="rg2-chart-empty">Sin datos para {label}</div>;

  const W = 600, H = 140, PAD = { t: 16, r: 8, b: 28, l: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const vals = data.map(p => p[metric] || 0);
  const maxVal = Math.max(...vals, 1);
  const barW = Math.max(4, innerW / data.length - 2);
  const step = innerW / data.length;

  const yTicks = 4;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const avgY = PAD.t + innerH - (avg / maxVal) * innerH;

  const showTip = (e, p) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: fmtDate(p.posted_at), value: fmtN(p[metric]) });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <ChartTooltip tip={tip} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const y = PAD.t + (innerH / yTicks) * i;
          const val = Math.round(maxVal - (maxVal / yTicks) * i);
          return (
            <g key={i}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">{fmtN(val)}</text>
            </g>
          );
        })}
        <line x1={PAD.l} x2={W - PAD.r} y1={avgY} y2={avgY}
          stroke={color} strokeWidth={0.8} strokeDasharray="4 3" opacity={0.5} />
        {data.map((p, i) => {
          const val = p[metric] || 0;
          const barH = (val / maxVal) * innerH;
          const x = PAD.l + i * step + (step - barW) / 2;
          const y = PAD.t + innerH - barH;
          return (
            <g key={p.id}
              onMouseEnter={e => showTip(e, p)}
              onMouseMove={e => showTip(e, p)}
              onMouseLeave={() => setTip(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect x={x} y={PAD.t} width={barW} height={innerH} fill="transparent" />
              <rect x={x} y={y} width={barW} height={barH} fill={color} opacity={0.8} rx={2} />
              {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
                <text x={x + barW / 2} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                  {fmtDate(p.posted_at)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Donut distribución tipos ── */
function TypeDonut({ posts }) {
  const counts = {};
  posts.forEach(p => { counts[p.media_type] = (counts[p.media_type] || 0) + 1; });
  const total = posts.length;
  if (!total) return null;

  const types = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const R = 45, CX = 60, CY = 60;
  let angle = -Math.PI / 2;

  const slices = types.map(([type, count]) => {
    const pct = count / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    const endAngle = angle;
    const x1 = CX + R * Math.cos(startAngle);
    const y1 = CY + R * Math.sin(startAngle);
    const x2 = CX + R * Math.cos(endAngle);
    const y2 = CY + R * Math.sin(endAngle);
    const large = pct > 0.5 ? 1 : 0;
    return { type, count, pct, x1, y1, x2, y2, large };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 120 120" style={{ width: 100, height: 100, flexShrink: 0 }}>
        {slices.map(s => (
          <path key={s.type}
            d={`M${CX},${CY} L${s.x1},${s.y1} A${R},${R} 0 ${s.large},1 ${s.x2},${s.y2} Z`}
            fill={TYPE_META[s.type]?.color || '#94a3b8'}
            stroke="#fff" strokeWidth={2}
          />
        ))}
        <circle cx={CX} cy={CY} r={24} fill="#fff" />
        <text x={CX} y={CY + 4} textAnchor="middle" fontSize={11} fontWeight="700" fill="#1e293b">{total}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {slices.map(s => (
          <div key={s.type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: TYPE_META[s.type]?.color || '#94a3b8', flexShrink: 0 }} />
            <span style={{ color: '#475569' }}>{TYPE_META[s.type]?.label || s.type}</span>
            <span style={{ color: '#94a3b8', marginLeft: 'auto', paddingLeft: 8 }}>
              {s.count} ({Math.round(s.pct * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── ER line chart ── */
function ErChart({ posts }) {
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  const data = posts
    .filter(p => p.engagement_rate != null)
    .slice().sort((a, b) => new Date(a.posted_at) - new Date(b.posted_at))
    .slice(-30);

  if (data.length < 2) return <div className="rg2-chart-empty">Pocos datos de ER</div>;

  const W = 600, H = 110, PAD = { t: 12, r: 8, b: 24, l: 40 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const vals = data.map(p => p.engagement_rate);
  const maxVal = Math.max(...vals, 0.01);
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;

  const pts = data.map((p, i) => {
    const x = PAD.l + (i / (data.length - 1)) * innerW;
    const y = PAD.t + innerH - (p.engagement_rate / maxVal) * innerH;
    return { x, y, p };
  });

  const polyline = pts.map(pt => `${pt.x},${pt.y}`).join(' ');
  const area = `M${pts[0].x},${PAD.t + innerH} ` +
    pts.map(pt => `L${pt.x},${pt.y}`).join(' ') +
    ` L${pts[pts.length-1].x},${PAD.t + innerH} Z`;
  const avgY = PAD.t + innerH - (avg / maxVal) * innerH;

  const showTip = (e, pt) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: fmtDate(pt.p.posted_at), value: fmtPct(pt.p.engagement_rate) });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <ChartTooltip tip={tip} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
        <defs>
          <linearGradient id="erGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00c9a7" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#00c9a7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const y = PAD.t + innerH * f;
          const val = maxVal * (1 - f);
          return (
            <g key={i}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">{val.toFixed(2)}%</text>
            </g>
          );
        })}
        <line x1={PAD.l} x2={W - PAD.r} y1={avgY} y2={avgY}
          stroke="#00694f" strokeWidth={0.8} strokeDasharray="4 3" opacity={0.4} />
        <path d={area} fill="url(#erGrad)" />
        <polyline points={polyline} fill="none" stroke="#00694f" strokeWidth={1.5} strokeLinejoin="round" />
        {pts.map((pt, i) => (
          <g key={i} onMouseEnter={e => showTip(e, pt)} onMouseMove={e => showTip(e, pt)} onMouseLeave={() => setTip(null)} style={{ cursor: 'pointer' }}>
            <circle cx={pt.x} cy={pt.y} r={6} fill="transparent" />
            <circle cx={pt.x} cy={pt.y} r={2} fill="#00694f" />
            {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
              <text x={pt.x} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                {fmtDate(pt.p.posted_at)}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ── Top posts ── */
function TopPosts({ posts, metric, label, color, badgeLabel }) {
  const sorted = [...posts]
    .filter(p => p[metric] != null)
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
    .slice(0, 5);

  if (!sorted.length) return null;
  const max = sorted[0][metric] || 1;

  return (
    <div>
      {sorted.map((p, i) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#94a3b8', width: 14 }}>{i + 1}</span>
          {p.media_type ? <TypeBadge mediaType={p.media_type} /> : (
            badgeLabel ? <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:'#fce7f3',color:'#be185d' }}>{badgeLabel}</span> : null
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${((p[metric] || 0) / max) * 100}%`, background: color, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fmtDate(p.posted_at)} · {(p.caption || '—').slice(0, 50)}
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
            {fmtN(p[metric])}
          </span>
          {p.permalink && (
            <a href={p.permalink} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: '#6366f1', textDecoration: 'none' }}>↗</a>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Panel de análisis de un perfil ── */
function ProfileReport({ profile, since, until }) {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [loaded, setLoaded]   = useState(false);
  const [chartTab, setChartTab] = useState('likes');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { since, until, limit: 500, ordering: '-posted_at' };
      if (!profile.is_marca) params.profile_id = profile.id;
      const res = await contentService.getMarcaPosts(params);
      setPosts(res.data.posts || []);
      setLoaded(true);
    } catch (e) {
      setError('Error al cargar los posts.');
    } finally {
      setLoading(false);
    }
  }, [profile.id, profile.is_marca, since, until]);

  useEffect(() => { load(); }, [load]);

  // KPIs
  const kpis = useMemo(() => {
    if (!posts.length) return null;
    const totLikes    = posts.reduce((s, p) => s + (p.like_count || 0), 0);
    const totComments = posts.reduce((s, p) => s + (p.comments_count || 0), 0);
    const totViews    = posts.reduce((s, p) => s + (p.views_count || 0), 0);
    // Con pauta (organico + ads); solo suma si Meta entregó el dato para ese post
    const totLikesPauta    = posts.reduce((s, p) => s + (p.total_like_count || p.like_count || 0), 0);
    const totCommentsPauta = posts.reduce((s, p) => s + (p.total_comments_count || p.comments_count || 0), 0);
    const totViewsPauta    = posts.reduce((s, p) => s + (p.total_views_count || p.views_count || 0), 0);
    const erList = posts.filter(p => p.engagement_rate != null).map(p => p.engagement_rate);
    const vrList = posts.filter(p => p.view_rate != null).map(p => p.view_rate);
    const reels  = posts.filter(p => p.media_type === 'VIDEO');
    return {
      total:       posts.length,
      totLikes, totComments, totViews,
      totLikesPauta, totCommentsPauta, totViewsPauta,
      avgEr:  erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
      avgVr:  vrList.length ? vrList.reduce((a, b) => a + b, 0) / vrList.length : null,
      reels:  reels.length,
      fotos:  posts.filter(p => p.media_type === 'IMAGE').length,
      carruseles: posts.filter(p => p.media_type === 'CAROUSEL_ALBUM').length,
    };
  }, [posts]);

  const CHART_OPTS = [
    { key: 'like_count',     label: 'Likes',        color: '#e1306c' },
    { key: 'comments_count', label: 'Comentarios',  color: '#6366f1' },
    { key: 'views_count',    label: 'Vistas',       color: '#0891b2' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando posts de @${profile.username}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (loaded && !posts.length) return (
    <div className="rg2-empty">No hay posts de @{profile.username} en este periodo.</div>
  );
  if (!loaded) return null;

  return (
    <div className="rg2-report">
      {/* KPIs */}
      <div className="rg2-kpis">
        <KpiCard label="Posts"       value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c"
          sub={kpis.totLikesPauta > kpis.totLikes ? `+pauta: ${fmtN(kpis.totLikesPauta)}` : null} />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1"
          sub={kpis.totCommentsPauta > kpis.totComments ? `+pauta: ${fmtN(kpis.totCommentsPauta)}` : null} />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2"
          sub={kpis.totViewsPauta > kpis.totViews ? `+pauta: ${fmtN(kpis.totViewsPauta)}` : null} />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#10b981"
          sub={kpis.avgVr != null ? `VR reels: ${fmtPct(kpis.avgVr)}` : null} />
        <KpiCard label="Reels"       value={kpis.reels}  color="#e1306c" />
        <KpiCard label="Fotos"       value={kpis.fotos}  color="#1565c0" />
        <KpiCard label="Carruseles"  value={kpis.carruseles} color="#4527a0" />
      </div>

      {/* Gráficos */}
      <div className="rg2-charts-grid">
        {/* Barras de métrica */}
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por post</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={posts} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        {/* ER Line */}
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por post</span>
          </div>
          <ErChart posts={posts} />
        </div>

        {/* Donut */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Distribución de contenido</span>
          </div>
          <TypeDonut posts={posts} />
        </div>

        {/* Top por likes */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por likes</span>
          </div>
          <TopPosts posts={posts} metric="like_count" label="Likes" color="#e1306c" />
        </div>

        {/* Top por vistas */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top reels por vistas</span>
          </div>
          <TopPosts posts={posts.filter(p => p.media_type === 'VIDEO')} metric="views_count" label="Vistas" color="#0891b2" />
        </div>

        {/* Top por comentarios */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por comentarios</span>
          </div>
          <TopPosts posts={posts} metric="comments_count" label="Coment." color="#6366f1" />
        </div>

        {/* Top por ER */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por ER</span>
          </div>
          <TopPosts posts={posts} metric="engagement_rate" label="ER" color="#10b981" />
        </div>
      </div>

      {/* Tabla */}
      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">ER</th>
              <th className="rg2-num">VR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <TypeBadge mediaType={p.media_type} />
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {p.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(p.posted_at)}</td>
                <td className="rg2-num">{fmtN(p.like_count)}</td>
                <td className="rg2-num">{fmtN(p.comments_count)}</td>
                <td className="rg2-num">{fmtN(p.views_count)}</td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(p.engagement_rate)}</td>
                <td className="rg2-num" style={{ color: '#00c9a7', fontWeight: 700 }}>
                  {p.view_rate != null ? fmtPct(p.view_rate) : <span style={{ color: '#cdd5d3' }}>—</span>}
                </td>
                <td>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                      Ver ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Gráfico de evolución de seguidores (todos los perfiles del mercado) ── */
function FollowersChart({ since, until, profileId }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [tip,     setTip]     = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const params = { since, until };
    if (profileId) params.profile_id = profileId;
    contentService.getMercadoFollowersHistory(params)
      .then(r => setData(r.data.profiles || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [since, until, profileId]);

  if (loading) return <Loading fullScreen={false} mensaje="Cargando" />;

  // Build 3 series from the first (and only when profileId is set) profile
  const profile = data[0];
  const igSnaps = profile?.snapshots_instagram || profile?.snapshots || [];
  const tkSnaps = profile?.snapshots_tiktok    || [];

  // Merge dates from both platforms to compute Total
  const allDatesSet = new Set([...igSnaps.map(s => s.date), ...tkSnaps.map(s => s.date)]);
  const allDates = [...allDatesSet].sort();

  const igByDate = Object.fromEntries(igSnaps.map(s => [s.date, s.followers_count]));
  const tkByDate = Object.fromEntries(tkSnaps.map(s => [s.date, s.followers_count]));

  // Build total series: only dates where at least one platform has data; sum what's available
  const totalSnaps = allDates
    .filter(d => igByDate[d] != null || tkByDate[d] != null)
    .map(d => ({ date: d, followers_count: (igByDate[d] || 0) + (tkByDate[d] || 0) }));

  const SERIES = [
    { key: 'ig',    label: 'Instagram', color: '#e1306c', snaps: igSnaps,    dashed: false },
    { key: 'tk',    label: 'TikTok',    color: '#010101', snaps: tkSnaps,    dashed: false },
    { key: 'total', label: 'Total',     color: '#00c9a7', snaps: totalSnaps, dashed: true  },
  ].filter(s => s.snaps.length >= 1);

  if (!SERIES.length || allDates.length < 2) return (
    <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
      <div className="rg2-chart-header">
        <span className="rg2-chart-title">Evolución de seguidores</span>
      </div>
      <div className="rg2-chart-empty">Aún no hay datos históricos — se irán acumulando con cada ronda del scraper.</div>
    </div>
  );

  const W = 700, H = 200, PAD = { t: 16, r: 60, b: 28, l: 60 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const allVals = SERIES.flatMap(s => s.snaps.map(sn => sn.followers_count));
  const dataMin = Math.min(...allVals);
  const dataMax = Math.max(...allVals);
  const pad = Math.max((dataMax - dataMin) * 0.5, dataMax * 0.002, 100);
  const minVal = dataMin - pad;
  const maxVal = dataMax + pad;
  const range  = maxVal - minVal;

  // Base value for % axis: first total or first ig snapshot
  const baseVal = totalSnaps[0]?.followers_count || igSnaps[0]?.followers_count || dataMin;

  const toX = (date) => {
    const idx = allDates.indexOf(date);
    return PAD.l + (idx / Math.max(allDates.length - 1, 1)) * innerW;
  };
  const toY = (val) => PAD.t + innerH - ((val - minVal) / range) * innerH;

  const yTicks = 4;

  const showTip = (e, label, value) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label, value });
  };

  return (
    <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
      <div className="rg2-chart-header">
        <span className="rg2-chart-title">Evolución de seguidores</span>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {SERIES.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 14, height: 2, borderRadius: 1, background: s.color,
                borderBottom: s.dashed ? `2px dashed ${s.color}` : 'none',
                height: s.dashed ? 0 : 2 }} />
              <span style={{ color: '#4a5854', fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <ChartTooltip tip={tip} />
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
          <defs>
            {SERIES.filter(s => s.dashed).map(s => (
              <marker key={s.key} id={`dash-${s.key}`} />
            ))}
          </defs>

          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const frac = i / yTicks;
            const y = PAD.t + innerH * frac;
            const val = maxVal - range * frac;
            const pct = ((val - baseVal) / baseVal * 100);
            return (
              <g key={i}>
                <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
                <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">
                  {fmtAxis(Math.round(val), dataMax - dataMin)}
                </text>
                <text x={W - PAD.r + 4} y={y + 4} textAnchor="start" fontSize={7}
                  fill={pct > 0 ? '#00694f' : pct < 0 ? '#ef4444' : '#94a3b8'}>
                  {pct > 0 ? '+' : ''}{pct.toFixed(2)}%
                </text>
              </g>
            );
          })}

          {SERIES.map(s => {
            const pts = s.snaps.map(sn => ({ x: toX(sn.date), y: toY(sn.followers_count), sn }));
            const polyline = pts.map(pt => `${pt.x},${pt.y}`).join(' ');
            return (
              <g key={s.key}>
                <polyline
                  points={polyline}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.key === 'total' ? 1 : 1.5}
                  strokeDasharray={s.dashed ? '4 3' : undefined}
                  strokeLinejoin="round"
                  opacity={s.key === 'total' ? 0.85 : 1}
                />
                {pts.map((pt, i) => (
                  <g key={i}
                    onMouseEnter={e => showTip(e, `${s.label} · ${fmtDate(pt.sn.date)}`, `${Number(pt.sn.followers_count).toLocaleString('es-CL')} seg.`)}
                    onMouseMove={e  => showTip(e, `${s.label} · ${fmtDate(pt.sn.date)}`, `${Number(pt.sn.followers_count).toLocaleString('es-CL')} seg.`)}
                    onMouseLeave={() => setTip(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx={pt.x} cy={pt.y} r={6} fill="transparent" />
                    <circle cx={pt.x} cy={pt.y} r={s.key === 'total' ? 1.5 : 2} fill={s.color} opacity={s.key === 'total' ? 0.7 : 1} />
                  </g>
                ))}
              </g>
            );
          })}

          {/* Eje X fechas */}
          {allDates.map((d, i) => (
            (i === 0 || i === allDates.length - 1 || i % Math.max(1, Math.floor(allDates.length / 5)) === 0) && (
              <text key={d} x={toX(d)} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                {fmtDate(d)}
              </text>
            )
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ── Panel TikTok de un perfil ── */
function TikTokReport({ profile, since, until }) {
  const [videos, setVideos]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [loaded, setLoaded]     = useState(false);
  const [chartTab, setChartTab] = useState('like_count');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { profile_id: profile.id, since, until, limit: 500, ordering: '-posted_at' };
      const res = await contentService.getMercadoTikTokPosts(params);
      setVideos(res.data.posts || []);
      setLoaded(true);
    } catch (e) {
      setError('Error al cargar los videos de TikTok.');
    } finally {
      setLoading(false);
    }
  }, [profile.id, since, until]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!videos.length) return null;
    const totLikes    = videos.reduce((s, v) => s + (v.like_count || 0), 0);
    const totComments = videos.reduce((s, v) => s + (v.comments_count || 0), 0);
    const totShares   = videos.reduce((s, v) => s + (v.share_count || 0), 0);
    const totViews    = videos.reduce((s, v) => s + (v.views_count || 0), 0);
    const totSaves    = videos.reduce((s, v) => s + (v.saves_count || 0), 0);
    const erList = videos.filter(v => v.engagement_rate != null).map(v => v.engagement_rate);
    const vrList = videos.filter(v => v.view_rate != null).map(v => v.view_rate);
    return {
      total: videos.length,
      totLikes, totComments, totShares, totViews, totSaves,
      avgEr: erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
      avgVr: vrList.length ? vrList.reduce((a, b) => a + b, 0) / vrList.length : null,
    };
  }, [videos]);

  const CHART_OPTS = [
    { key: 'like_count',      label: 'Likes',       color: '#e1306c' },
    { key: 'views_count',     label: 'Vistas',      color: '#0891b2' },
    { key: 'comments_count',  label: 'Comentarios', color: '#6366f1' },
    { key: 'share_count',     label: 'Shares',      color: '#f59e0b' },
    { key: 'saves_count',     label: 'Guardados',   color: '#10b981' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando TikTok de @${profile.tiktok_username}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (loaded && !videos.length) return (
    <div className="rg2-empty">No hay videos de @{profile.tiktok_username} en este periodo.</div>
  );
  if (!loaded) return null;

  return (
    <div className="rg2-report">
      {/* KPIs */}
      <div className="rg2-kpis">
        <KpiCard label="Videos"      value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c" />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1" />
        <KpiCard label="Shares"      value={fmtN(kpis.totShares)}   color="#f59e0b" />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2" />
        <KpiCard label="Guardados"   value={fmtN(kpis.totSaves)}    color="#10b981" />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#8b5cf6"
          sub={kpis.avgVr != null ? `VR: ${fmtPct(kpis.avgVr)}` : null} />
      </div>

      {/* Gráficos */}
      <div className="rg2-charts-grid">
        {/* Barras */}
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por video</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={videos} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        {/* ER Line */}
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por video</span>
          </div>
          <ErChart posts={videos} />
        </div>

        {/* Top por vistas */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por vistas</span>
          </div>
          <TopPosts posts={videos} metric="views_count" label="Vistas" color="#0891b2" badgeLabel="TikTok" />
        </div>

        {/* Top por likes */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por likes</span>
          </div>
          <TopPosts posts={videos} metric="like_count" label="Likes" color="#e1306c" badgeLabel="TikTok" />
        </div>

        {/* Top por ER */}
        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por ER</span>
          </div>
          <TopPosts posts={videos} metric="engagement_rate" label="ER" color="#8b5cf6" badgeLabel="TikTok" />
        </div>
      </div>

      {/* Tabla */}
      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Video</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Shares</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">Guardados</th>
              <th className="rg2-num">ER</th>
              <th className="rg2-num">VR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {videos.map(v => (
              <tr key={v.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {v.cover_url ? (
                      <img src={v.cover_url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fce7f3', color: '#be185d' }}>
                        {v.is_pinned ? '📌 TikTok' : 'TikTok'}
                      </span>
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {v.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(v.posted_at)}</td>
                <td className="rg2-num">{fmtN(v.like_count)}</td>
                <td className="rg2-num">{fmtN(v.comments_count)}</td>
                <td className="rg2-num">{fmtN(v.share_count)}</td>
                <td className="rg2-num">{fmtN(v.views_count)}</td>
                <td className="rg2-num">{fmtN(v.saves_count)}</td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(v.engagement_rate)}</td>
                <td className="rg2-num" style={{ color: '#00c9a7', fontWeight: 700 }}>
                  {v.view_rate != null ? fmtPct(v.view_rate) : <span style={{ color: '#cdd5d3' }}>—</span>}
                </td>
                <td>
                  {v.permalink && (
                    <a href={v.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                      Ver ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Gráfico simple de evolución de seguidores (genérico, historia = [{fecha, seguidores}]) ── */
function SeguidoresLineChart({ history, platformLabel = '', color = '#e1306c' }) {
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);
  const W = 700, H = 200, PAD = { t: 16, r: 60, b: 28, l: 60 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const allVals  = history.map(s => s.seguidores).filter(v => v != null);
  const dataMin  = allVals.length ? Math.min(...allVals) : 0;
  const dataMax  = allVals.length ? Math.max(...allVals) : 1;
  const padY     = Math.max((dataMax - dataMin) * 0.5, dataMax * 0.002, 10);
  const minVal   = dataMin - padY;
  const maxVal   = dataMax + padY;
  const rangeVal = maxVal - minVal || 1;
  const toX = (i) => PAD.l + (i / Math.max(history.length - 1, 1)) * innerW;
  const toY = (v) => PAD.t + innerH - ((v - minVal) / rangeVal) * innerH;
  const pts = history.map((s, i) => s.seguidores != null ? `${toX(i)},${toY(s.seguidores)}` : null).filter(Boolean).join(' ');

  return (
    <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
      <div className="rg2-chart-header">
        <span className="rg2-chart-title">Evolución de seguidores</span>
        {platformLabel && <span style={{ fontSize: 11, color: '#4a5854', fontWeight: 600 }}>{platformLabel}</span>}
      </div>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <ChartTooltip tip={tip} />
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
          {[0, 1, 2, 3, 4].map(i => {
            const v = minVal + (rangeVal * i / 4);
            const y = toY(v);
            return (
              <g key={i}>
                <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#e2e8f0" strokeWidth={0.5} />
                <text x={PAD.l - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">{fmtN(Math.round(v))}</text>
              </g>
            );
          })}
          <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />
          {history.map((s, i) => s.seguidores != null && (
            <circle key={i} cx={toX(i)} cy={toY(s.seguidores)} r={3.5} fill={color}
              onMouseEnter={() => {
                const rect = wrapRef.current?.getBoundingClientRect();
                setTip({
                  x: rect ? (toX(i) / W) * rect.width : 0,
                  y: rect ? (toY(s.seguidores) / H) * rect.height : 0,
                  label: fmtDate(s.fecha),
                  value: `${fmtN(s.seguidores)} seguidores`,
                });
              }}
            />
          ))}
          {history.map((s, i) => (
            i === 0 || i === history.length - 1 || history.length <= 6 ? (
              <text key={`x-${i}`} x={toX(i)} y={H - 6} textAnchor="middle" fontSize={9} fill="#94a3b8">
                {fmtDate(s.fecha)}
              </text>
            ) : null
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ── Reporte TikTok de cuenta propia conectada (OAuth, tracked-accounts) ──
   Análogo a MetaDbReport pero para la cuenta TikTok vinculada vía
   Configuración → Cuentas Conectadas TikTok. Usa los mismos endpoints que
   Analytics TikTok (getMyTikTokAccount + getTrackedTikTokAccounts +
   getTikTokAccountPosts) y normaliza los campos al shape que ya esperan
   BarChart/TopPosts/ErChart (like_count, comments_count, views_count,
   share_count, saves_count, posted_at, cover_url, permalink, engagement_rate). ── */
function TikTokOwnReport({ oauthAccount, trackedAccountId, since, until, marcaProfile = null, mergeMarca = false }) {
  const [videos, setVideos]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [loaded, setLoaded]     = useState(false);
  const [chartTab, setChartTab] = useState('like_count');
  const [marcaAddedCount, setMarcaAddedCount] = useState(0);
  const [followersHistory, setFollowersHistory] = useState([]);

  useEffect(() => {
    if (!trackedAccountId) { setFollowersHistory([]); return; }
    const days = Math.max(1, Math.ceil((new Date(until) - new Date(since)) / 86400000) + 1);
    contentService.getTikTokFollowersHistory(trackedAccountId, Math.max(days, 90))
      .then(r => setFollowersHistory(r.data.history || []))
      .catch(() => setFollowersHistory([]));
  }, [trackedAccountId, since, until]);

  // Complementa con posts de TikTok de "Mi Marca" (scraper) que no estén ya
  // en `base` (cuenta OAuth conectada) — matcheados por video_id (el ID de
  // TikTok es el mismo sin importar la fuente: API oficial o scraper).
  const fetchMarcaExtras = useCallback(async (base) => {
    if (!mergeMarca || !marcaProfile?.tiktok_username) return base;
    try {
      const res = await contentService.getMercadoTikTokPosts({
        profile_id: marcaProfile.id, since, until, limit: 500,
      });
      const knownIds = new Set(base.map(v => String(v.id)));
      const extras = (res.data.posts || [])
        .filter(p => !knownIds.has(String(p.video_id)))
        .map(p => ({
          id:               p.video_id,
          cover_url:        p.cover_url,
          caption:          p.caption,
          posted_at:        p.posted_at,
          like_count:       p.like_count || 0,
          comments_count:   p.comments_count || 0,
          share_count:      p.share_count || 0,
          views_count:      p.views_count || 0,
          saves_count:      p.saves_count || 0,
          permalink:        p.permalink,
          engagement_rate:  p.engagement_rate,
          _from_marca:      true,
        }));
      setMarcaAddedCount(extras.length);
      return [...base, ...extras];
    } catch (e) {
      return base; // si falla Mi Marca, igual mostramos lo de la cuenta OAuth
    }
  }, [mergeMarca, marcaProfile, since, until]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    setMarcaAddedCount(0);
    try {
      let normalized = [];
      if (trackedAccountId) {
        const days = Math.max(1, Math.ceil((new Date(until) - new Date(since)) / 86400000) + 1);
        const res = await contentService.getTikTokAccountPosts(trackedAccountId, days);
        const byVideo = new Map();
        for (const p of (res.data.posts || [])) {
          const prev = byVideo.get(p.video_id);
          if (!prev || new Date(p.snapshot_timestamp) > new Date(prev.snapshot_timestamp)) {
            byVideo.set(p.video_id, p);
          }
        }
        const sinceD = since ? new Date(since) : null;
        const untilD = until ? new Date(until) : null;
        normalized = Array.from(byVideo.values())
          .filter(p => {
            const d = p.created_time ? new Date(p.created_time) : null;
            if (!d) return true;
            if (sinceD && d < sinceD) return false;
            if (untilD && d > untilD) return false;
            return true;
          })
          .map(p => {
            const views = p.view_count || 0;
            const eng   = (p.like_count || 0) + (p.comment_count || 0) + (p.share_count || 0);
            return {
              id:               p.video_id,
              cover_url:        p.video_cover,
              caption:          p.caption,
              posted_at:        p.created_time,
              like_count:       p.like_count,
              comments_count:   p.comment_count,
              share_count:      p.share_count,
              views_count:      views,
              saves_count:      p.save_count || 0,
              permalink:        p.video_url,
              engagement_rate:  views > 0 ? Number(((eng / views) * 100).toFixed(2)) : null,
            };
          });
      }
      normalized = await fetchMarcaExtras(normalized);
      setVideos(normalized);
      setLoaded(true);
    } catch (e) {
      setError('Error al cargar los videos de TikTok.');
    } finally {
      setLoading(false);
    }
  }, [trackedAccountId, since, until, fetchMarcaExtras]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!videos.length) return null;
    const totLikes    = videos.reduce((s, v) => s + (v.like_count || 0), 0);
    const totComments = videos.reduce((s, v) => s + (v.comments_count || 0), 0);
    const totShares   = videos.reduce((s, v) => s + (v.share_count || 0), 0);
    const totViews    = videos.reduce((s, v) => s + (v.views_count || 0), 0);
    const totSaves    = videos.reduce((s, v) => s + (v.saves_count || 0), 0);
    const erList = videos.filter(v => v.engagement_rate != null).map(v => v.engagement_rate);
    return {
      total: videos.length,
      totLikes, totComments, totShares, totViews, totSaves,
      avgEr: erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
    };
  }, [videos]);

  const CHART_OPTS = [
    { key: 'like_count',      label: 'Likes',       color: '#e1306c' },
    { key: 'views_count',     label: 'Vistas',      color: '#0891b2' },
    { key: 'comments_count',  label: 'Comentarios', color: '#6366f1' },
    { key: 'share_count',     label: 'Shares',      color: '#f59e0b' },
    { key: 'saves_count',     label: 'Guardados',   color: '#10b981' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (!trackedAccountId && !mergeMarca) return (
    <div className="rg2-empty">
      Aún no hay videos trackeados para @{oauthAccount?.username}. El sistema recolecta métricas automáticamente
      cada 10 minutos tras conectar la cuenta — vuelve a intentar en unos minutos.
    </div>
  );
  if (loading) return <Loading fullScreen={false} mensaje="Cargando TikTok" />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (loaded && !videos.length) return (
    <div className="rg2-empty">No hay videos de @{oauthAccount?.username} en este periodo.</div>
  );
  if (!loaded) return null;

  return (
    <div className="rg2-report">
      {followersHistory.length >= 2 && (
        <SeguidoresLineChart history={followersHistory.map(h => ({ fecha: h.fecha, seguidores: h.seguidores }))} platformLabel="TikTok" color="#000" />
      )}
      {mergeMarca && marcaAddedCount > 0 && (
        <div style={{
          marginBottom: 14, padding: '8px 14px', borderRadius: 8,
          background: '#fff7ed', border: '1px solid #fed7aa',
          fontSize: 12, color: '#9a3412', fontWeight: 600,
        }}>
          ＋{marcaAddedCount} video{marcaAddedCount === 1 ? '' : 's'} de "Mi Marca" agregado{marcaAddedCount === 1 ? '' : 's'}
          {' '}(no capturados por la cuenta TikTok conectada). Se complementan las métricas de ambas fuentes.
        </div>
      )}

      {/* KPIs */}
      <div className="rg2-kpis">
        <KpiCard label="Videos"      value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c" />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1" />
        <KpiCard label="Shares"      value={fmtN(kpis.totShares)}   color="#f59e0b" />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2" />
        <KpiCard label="Guardados"   value={fmtN(kpis.totSaves)}    color="#10b981" />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#8b5cf6" />
      </div>

      {/* Gráficos */}
      <div className="rg2-charts-grid">
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por video</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={videos} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por video</span>
          </div>
          <ErChart posts={videos} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por vistas</span>
          </div>
          <TopPosts posts={videos} metric="views_count" label="Vistas" color="#0891b2" badgeLabel="TikTok" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por likes</span>
          </div>
          <TopPosts posts={videos} metric="like_count" label="Likes" color="#e1306c" badgeLabel="TikTok" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por ER</span>
          </div>
          <TopPosts posts={videos} metric="engagement_rate" label="ER" color="#8b5cf6" badgeLabel="TikTok" />
        </div>
      </div>

      {/* Tabla */}
      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Video</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Shares</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">Guardados</th>
              <th className="rg2-num">ER</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {videos.map(v => (
              <tr key={v.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {v.cover_url ? (
                      <img src={v.cover_url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fce7f3', color: '#be185d' }}>
                        TikTok
                      </span>
                      {v._from_marca && (
                        <span style={{
                          display: 'inline-block', marginLeft: 4, padding: '2px 8px', borderRadius: 20,
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                          background: '#fff7ed', color: '#9a3412',
                        }} title="Video de 'Mi Marca' no capturado por la cuenta TikTok conectada">Mi Marca</span>
                      )}
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {v.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(v.posted_at)}</td>
                <td className="rg2-num">{fmtN(v.like_count)}</td>
                <td className="rg2-num">{fmtN(v.comments_count)}</td>
                <td className="rg2-num">{fmtN(v.share_count)}</td>
                <td className="rg2-num">{fmtN(v.views_count)}</td>
                <td className="rg2-num">{fmtN(v.saves_count)}</td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(v.engagement_rate)}</td>
                <td>
                  {v.permalink && (
                    <a href={v.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                      Ver ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Reporte Facebook desde DB (MetaFBSnapshotDia) ──
   Análogo a MetaDbReport pero para la Página Facebook conectada vía OAuth.
   Usa meta_fb_db (snapshots grabados desde MetaAnalytics tab Facebook). ── */
function FacebookDbReport({ pagina, since, until }) {
  const [posts, setPosts]       = useState([]);
  const [fansHistory, setFansHistory] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [chartTab, setChartTab] = useState('likes');

  useEffect(() => {
    setLoading(true); setError(null);
    contentService.getFbDbData(since, until, pagina?.page_id || '')
      .then(r => {
        setPosts(r.data.posts || []);
        setFansHistory(r.data.fans_history || []);
      })
      .catch(e => {
        if (e?.response?.status === 404) setPosts([]);
        else setError('Error al cargar datos de Facebook.');
      })
      .finally(() => setLoading(false));
  }, [pagina, since, until]);

  const kpis = useMemo(() => {
    if (!posts.length) return null;
    const totLikes    = posts.reduce((s, p) => s + (p.likes || 0), 0);
    const totComments = posts.reduce((s, p) => s + (p.comments || 0), 0);
    const totShares    = posts.reduce((s, p) => s + (p.shares || 0), 0);
    const totReactions = posts.reduce((s, p) => s + (p.reactions_total || 0), 0);
    const totImpressions       = posts.reduce((s, p) => s + (p.post_impressions || 0), 0);
    const totImpressionsUnique = posts.reduce((s, p) => s + (p.post_impressions_unique || 0), 0);
    const totEngaged           = posts.reduce((s, p) => s + (p.post_engaged_users || 0), 0);
    return {
      total: posts.length,
      totLikes, totComments, totShares, totReactions,
      totImpressions, totImpressionsUnique, totEngaged,
    };
  }, [posts]);

  const CHART_OPTS = [
    { key: 'likes',                   label: 'Likes',       color: '#e1306c' },
    { key: 'comments',                label: 'Comentarios', color: '#6366f1' },
    { key: 'shares',                  label: 'Compartidos', color: '#14b8a6' },
    { key: 'post_impressions_unique', label: 'Alcance',     color: '#8b5cf6' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando Facebook de ${pagina?.nombre}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (!posts.length) return <div className="rg2-empty">No hay posts de Facebook en este periodo.</div>;

  return (
    <div className="rg2-report">
      {/* KPIs */}
      <div className="rg2-kpis">
        <KpiCard label="Posts"       value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}       color="#e1306c" />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)}    color="#6366f1" />
        <KpiCard label="Compartidos" value={fmtN(kpis.totShares)}      color="#14b8a6" />
        <KpiCard label="Reacciones"  value={fmtN(kpis.totReactions)}   color="#f59e0b" />
        <KpiCard label="Alcance"     value={fmtN(kpis.totImpressionsUnique)} color="#8b5cf6" />
        <KpiCard label="Impresiones" value={fmtN(kpis.totImpressions)} color="#0891b2" />
      </div>

      {/* Gráficos */}
      <div className="rg2-charts-grid">
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por post</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={posts.map(p => ({ ...p, posted_at: p.posted_at }))} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por alcance</span>
          </div>
          <TopPosts posts={posts} metric="post_impressions_unique" label="Alcance" color="#8b5cf6" badgeLabel="Facebook" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por likes</span>
          </div>
          <TopPosts posts={posts} metric="likes" label="Likes" color="#e1306c" badgeLabel="Facebook" />
        </div>
      </div>

      {/* Tabla */}
      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Compart.</th>
              <th className="rg2-num">Alcance</th>
              <th className="rg2-num">Impresiones</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.picture ? (
                      <img src={p.picture} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1e40af' }}>
                        Facebook
                      </span>
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {p.message || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin mensaje</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(p.posted_at)}</td>
                <td className="rg2-num">{fmtN(p.likes)}</td>
                <td className="rg2-num">{fmtN(p.comments)}</td>
                <td className="rg2-num">{fmtN(p.shares)}</td>
                <td className="rg2-num">{fmtN(p.post_impressions_unique)}</td>
                <td className="rg2-num">{fmtN(p.post_impressions)}</td>
                <td>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Ver ↗</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Reporte desde DB (MetaIGSnapshotDia) ──
   Si mergeMarca=true, además agrega los posts de "Mi Marca" (scraper) que META
   NO trae — típicamente collabs donde la cuenta es tageada pero no es la
   "creadora" del post, por lo que la Graph API no los devuelve. El match entre
   ambas fuentes se hace por shortcode extraído del permalink. Estos posts
   agregados no tienen reach/saved/shares (el scraper no los captura), así que
   suman 0 en esas métricas — se marcan con un badge "Collab" en la tabla. ── */
function MetaDbReport({ account, since, until, mergeMarca = false }) {
  const [posts, setPosts]       = useState([]);
  const [stories, setStories]   = useState([]);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [chartTab, setChartTab] = useState('like_count');
  const [section, setSection]   = useState('posts'); // 'posts' | 'stories'
  const [marcaAddedCount, setMarcaAddedCount] = useState(0);
  const wrapRef = useRef(null);
  const [tip, setTip]           = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    setMarcaAddedCount(0);

    const fetchMarcaExtras = (metaPosts) => {
      if (!mergeMarca) return metaPosts;
      const metaShortcodes = new Set(
        metaPosts.map(p => extractShortcode(p.permalink)).filter(Boolean)
      );
      return contentService.getMarcaPosts({ since, until, limit: 500 })
        .then(r => {
          const marcaPosts = r.data.posts || [];
          const extras = marcaPosts
            .filter(p => p.shortcode && !metaShortcodes.has(p.shortcode))
            .map(p => ({
              id:                `marca-${p.id}`,
              permalink:         p.permalink,
              thumbnail_url:     p.thumbnail_url,
              media_type:        p.media_type,
              media_product_type: p.media_type === 'VIDEO' ? 'REELS' : undefined,
              caption:           p.caption,
              posted_at:         p.posted_at,
              like_count:        p.like_count || 0,
              comments_count:    p.comments_count || 0,
              views_count:       p.views_count || 0,
              reach:             0,
              shares:            0,
              saved:             0,
              engagement_rate:   p.engagement_rate,
              view_rate:         p.view_rate,
              is_collab:         p.is_collab,
              collab_usernames:  p.collab_usernames,
              _from_marca:       true,
            }));
          setMarcaAddedCount(extras.length);
          return [...metaPosts, ...extras];
        })
        .catch(() => metaPosts); // si falla Mi Marca, igual mostramos lo de META
    };

    contentService.getIgDbData(500, since, until, account?.ig_user_id || '')
      .then(r => {
        const metaPosts = r.data.posts || [];
        return Promise.resolve(fetchMarcaExtras(metaPosts)).then(merged => {
          setPosts(merged);
          setStories(r.data.stories || []);
          setHistory(r.data.seguidores_history || []);
        });
      })
      .catch(e => {
        if (e?.response?.status === 404) {
          // Sin snapshots de META: si es el tab Reporte Marca, igual mostramos los posts de Mi Marca
          if (mergeMarca) {
            fetchMarcaExtras([]).then(merged => setPosts(merged));
          } else {
            setPosts([]);
          }
        } else {
          setError('Error al cargar datos de la base de datos.');
        }
      })
      .finally(() => setLoading(false));
  }, [since, until]);

  const kpis = useMemo(() => {
    if (!posts.length) return null;
    const totLikes    = posts.reduce((s, p) => s + (p.like_count || 0), 0);
    const totComments = posts.reduce((s, p) => s + (p.comments_count || 0), 0);
    const totViews    = posts.reduce((s, p) => s + (p.views_count || 0), 0);
    const totReach    = posts.reduce((s, p) => s + (p.reach || 0), 0);
    const totShares   = posts.reduce((s, p) => s + (p.shares || 0), 0);
    const totSaved    = posts.reduce((s, p) => s + (p.saved || 0), 0);
    // Con pauta (organico + ads); solo suma si Meta entregó el dato para ese post
    const totLikesPauta    = posts.reduce((s, p) => s + (p.total_like_count || p.like_count || 0), 0);
    const totCommentsPauta = posts.reduce((s, p) => s + (p.total_comments_count || p.comments_count || 0), 0);
    const totViewsPauta    = posts.reduce((s, p) => s + (p.total_views_count || p.views_count || 0), 0);
    const erList = posts.filter(p => p.engagement_rate != null).map(p => p.engagement_rate);
    const vrList = posts.filter(p => p.view_rate != null).map(p => p.view_rate);
    const reels = posts.filter(p => p.media_product_type === 'REELS');
    return {
      total: posts.length,
      totLikes, totComments, totViews, totReach, totShares, totSaved,
      totLikesPauta, totCommentsPauta, totViewsPauta,
      avgEr: erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
      avgVr: vrList.length ? vrList.reduce((a, b) => a + b, 0) / vrList.length : null,
      reels:      reels.length,
      fotos:      posts.filter(p => p.media_product_type !== 'REELS' && p.media_type === 'IMAGE').length,
      carruseles: posts.filter(p => p.media_type === 'CAROUSEL_ALBUM').length,
    };
  }, [posts]);

  // Stories KPIs — debe estar antes de cualquier early return
  const storiesKpis = useMemo(() => {
    if (!stories.length) return null;
    const totReach      = stories.reduce((s, st) => s + (st.reach || 0), 0);
    const totReplies    = stories.reduce((s, st) => s + (st.replies || 0), 0);
    const totNav        = stories.reduce((s, st) => s + (st.navigation || 0), 0);
    const totInteract   = stories.reduce((s, st) => s + (st.total_interactions || 0), 0);
    const totFollows    = stories.reduce((s, st) => s + (st.follows || 0), 0);
    const totProfVisits = stories.reduce((s, st) => s + (st.profile_visits || 0), 0);
    return { total: stories.length, totReach, totReplies, totNav, totInteract, totFollows, totProfVisits };
  }, [stories]);

  const CHART_OPTS = [
    { key: 'like_count',     label: 'Likes',       color: '#e1306c' },
    { key: 'comments_count', label: 'Comentarios', color: '#6366f1' },
    { key: 'views_count',    label: 'Vistas',      color: '#0891b2' },
    { key: 'reach',          label: 'Alcance',     color: '#8b5cf6' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  // Gráfico de evolución de seguidores desde snapshot DB
  const showFollowers = history.length >= 2;
  const W = 700, H = 200, PAD = { t: 16, r: 60, b: 28, l: 60 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const allVals  = history.map(s => s.seguidores).filter(Boolean);
  const dataMin  = allVals.length ? Math.min(...allVals) : 0;
  const dataMax  = allVals.length ? Math.max(...allVals) : 1;
  const padY     = Math.max((dataMax - dataMin) * 0.5, dataMax * 0.002, 100);
  const minVal   = dataMin - padY;
  const maxVal   = dataMax + padY;
  const rangeVal = maxVal - minVal || 1;
  const toX = (i) => PAD.l + (i / Math.max(history.length - 1, 1)) * innerW;
  const toY = (v) => PAD.t + innerH - ((v - minVal) / rangeVal) * innerH;
  const pts  = history.map((s, i) => s.seguidores ? `${toX(i)},${toY(s.seguidores)}` : null).filter(Boolean).join(' ');

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando datos de @${account.username}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (!posts.length) return (
    <div className="rg2-empty">No hay snapshots guardados para este período.<br />
    Ve a <strong>Analytics Meta</strong> y presiona <strong>GRABAR SNAPSHOT</strong> para guardar los datos.</div>
  );

  return (
    <div className="rg2-report">
      {/* Selector Posts / Stories */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ key: 'posts', label: 'Posts & Reels' }, { key: 'stories', label: `Stories guardadas${stories.length ? ` (${stories.length})` : ''}` }].map(opt => (
          <button key={opt.key} onClick={() => setSection(opt.key)} style={{
            padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: '0.01em',
            border: section === opt.key ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
            background: section === opt.key ? '#002421' : '#fff',
            color: section === opt.key ? '#fff' : '#4a5854',
          }}>{opt.label}</button>
        ))}
      </div>

      {/* ── SECCIÓN STORIES ── */}
      {section === 'stories' && (
        <div>
          {!stories.length ? (
            <div className="rg2-empty">No hay stories guardadas en este período.<br />
            Las stories se capturan automáticamente antes de expirar (24 h).</div>
          ) : (
            <>
              <div className="rg2-kpis">
                <KpiCard label="Stories"         value={storiesKpis.total} />
                <KpiCard label="Alcance total"   value={fmtN(storiesKpis.totReach)}      color="#8b5cf6" />
                <KpiCard label="Respuestas"      value={fmtN(storiesKpis.totReplies)}    color="#e1306c" />
                <KpiCard label="Navegación"      value={fmtN(storiesKpis.totNav)}        color="#0891b2" />
                <KpiCard label="Interacciones"   value={fmtN(storiesKpis.totInteract)}   color="#f59e0b" />
                <KpiCard label="Nuevos segs."    value={fmtN(storiesKpis.totFollows)}    color="#10b981" />
                <KpiCard label="Visitas perfil"  value={fmtN(storiesKpis.totProfVisits)} color="#14b8a6" />
              </div>
              <div className="rg2-table-wrap" style={{ marginTop: 16 }}>
                <table className="rg2-table">
                  <thead>
                    <tr>
                      <th>Story</th>
                      <th>Fecha</th>
                      <th className="rg2-num">Alcance</th>
                      <th className="rg2-num">Respuestas</th>
                      <th className="rg2-num">Navegación</th>
                      <th className="rg2-num">Interacciones</th>
                      <th className="rg2-num">Nuevos segs.</th>
                      <th className="rg2-num">Visitas perfil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...stories].sort((a, b) => new Date(b.timestamp || b.posted_at) - new Date(a.timestamp || a.posted_at)).map(st => (
                      <tr key={st.id} className="rg2-row">
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {st.media_url ? (
                              <img src={st.media_url} alt=""
                                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div style={{ width: 36, height: 36, background: '#fce7f3', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📸</div>
                            )}
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fce7f3', color: '#be185d' }}>
                              Story
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(st.timestamp || st.posted_at)}</td>
                        <td className="rg2-num">{fmtN(st.reach)}</td>
                        <td className="rg2-num">{fmtN(st.replies)}</td>
                        <td className="rg2-num">{fmtN(st.navigation)}</td>
                        <td className="rg2-num">{fmtN(st.total_interactions)}</td>
                        <td className="rg2-num">{fmtN(st.follows)}</td>
                        <td className="rg2-num">{fmtN(st.profile_visits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── SECCIÓN POSTS (original) ── */}
      {section === 'posts' && <>

      {/* Evolución seguidores */}
      {showFollowers && (
        <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución de seguidores</span>
            <span style={{ fontSize: 11, color: '#4a5854', fontWeight: 600 }}>Instagram</span>
          </div>
          <div ref={wrapRef} style={{ position: 'relative' }}>
            <ChartTooltip tip={tip} />
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
              {/* Grid lines */}
              {[0,1,2,3,4].map(i => {
                const v = minVal + (rangeVal * i / 4);
                const y = toY(v);
                return (
                  <g key={i}>
                    <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#e2e8f0" strokeWidth={0.5} />
                    <text x={PAD.l - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
                      {fmtN(Math.round(v))}
                    </text>
                  </g>
                );
              })}
              {/* Line */}
              <polyline points={pts} fill="none" stroke="#e1306c" strokeWidth={2} strokeLinejoin="round" />
              {/* Dots + labels */}
              {history.map((s, i) => s.seguidores ? (
                <circle key={i} cx={toX(i)} cy={toY(s.seguidores)} r={3} fill="#e1306c"
                  onMouseEnter={e => {
                    if (!wrapRef.current) return;
                    const rect = wrapRef.current.getBoundingClientRect();
                    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: s.fecha, value: fmtN(s.seguidores) });
                  }}
                  style={{ cursor: 'pointer' }}
                />
              ) : null)}
              {/* X labels every N */}
              {history.filter((_, i) => i % Math.max(1, Math.floor(history.length / 6)) === 0).map((s, i, arr) => {
                const origIdx = history.indexOf(s);
                return (
                  <text key={i} x={toX(origIdx)} y={H - 4} textAnchor="middle" fontSize={9} fill="#94a3b8">
                    {s.fecha?.slice(5)}
                  </text>
                );
              })}
            </svg>
          </div>
        </div>
      )}

      {mergeMarca && marcaAddedCount > 0 && (
        <div style={{
          marginBottom: 14, padding: '8px 14px', borderRadius: 8,
          background: '#fff7ed', border: '1px solid #fed7aa',
          fontSize: 12, color: '#9a3412', fontWeight: 600,
        }}>
          ＋{marcaAddedCount} post{marcaAddedCount === 1 ? '' : 's'} de "Mi Marca" agregado{marcaAddedCount === 1 ? '' : 's'}
          {' '}(collabs no capturados por META). No suman a Alcance / Guardados / Compartidos porque el scraper no trae esas métricas.
        </div>
      )}

      {/* KPIs */}
      {/* Row 1: métricas */}
      <div className="rg2-kpis">
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c"
          sub={kpis.totLikesPauta > kpis.totLikes ? `+pauta: ${fmtN(kpis.totLikesPauta)}` : null} />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1"
          sub={kpis.totCommentsPauta > kpis.totComments ? `+pauta: ${fmtN(kpis.totCommentsPauta)}` : null} />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2"
          sub={kpis.totViewsPauta > kpis.totViews ? `+pauta: ${fmtN(kpis.totViewsPauta)}` : null} />
        <KpiCard label="Alcance"     value={fmtN(kpis.totReach)}    color="#8b5cf6" />
        <KpiCard label="Guardados"   value={fmtN(kpis.totSaved)}    color="#f59e0b" />
        <KpiCard label="Compartidos" value={fmtN(kpis.totShares)}   color="#14b8a6" />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#10b981"
          sub={kpis.avgVr != null ? `VR reels: ${fmtPct(kpis.avgVr)}` : null} />
      </div>
      {/* Row 2: conteos de formatos */}
      <div className="rg2-kpis" style={{ marginTop: 8 }}>
        <KpiCard label="Posts"       value={kpis.total} />
        <KpiCard label="Reels"       value={kpis.reels}       color="#e1306c" />
        <KpiCard label="Fotos"       value={kpis.fotos}       color="#1565c0" />
        <KpiCard label="Carruseles"  value={kpis.carruseles}  color="#4527a0" />
      </div>

      {/* Gráficos */}
      <div className="rg2-charts-grid">
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por post</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >{o.label}</button>
              ))}
            </div>
          </div>
          <BarChart posts={posts} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por post</span>
          </div>
          <ErChart posts={posts} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Distribución de contenido</span>
          </div>
          <TypeDonut posts={posts} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por likes</span>
          </div>
          <TopPosts posts={posts} metric="like_count" label="Likes" color="#e1306c" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top reels por vistas</span>
          </div>
          <TopPosts posts={posts.filter(p => p.media_product_type === 'REELS')} metric="views_count" label="Vistas" color="#0891b2" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por alcance</span>
          </div>
          <TopPosts posts={posts} metric="reach" label="Alcance" color="#8b5cf6" />
        </div>
      </div>

      {/* Tabla */}
      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Alcance</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">ER</th>
              <th className="rg2-num">VR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.thumbnail_url || p.media_url ? (
                      <img src={p.thumbnail_url || p.media_url} alt=""
                        style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <TypeBadge mediaType={p.media_type} />
                      {p._from_marca && (
                        <span style={{
                          display: 'inline-block', marginLeft: 4, padding: '2px 8px', borderRadius: 20,
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                          background: '#fff7ed', color: '#9a3412',
                        }} title="Post de 'Mi Marca' no capturado por META (collab)">Collab</span>
                      )}
                      {p.is_boosted && (
                        <span style={{
                          display: 'inline-block', marginLeft: 4, padding: '2px 8px', borderRadius: 20,
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                          background: '#eef2ff', color: '#4338ca',
                        }} title={`Post orgánico con pauta activa (${p.active_ads_count} anuncio${p.active_ads_count === 1 ? '' : 's'} activo${p.active_ads_count === 1 ? '' : 's'})`}>Con pauta</span>
                      )}
                      {p.media_product_type === 'AD' && (
                        <span style={{
                          display: 'inline-block', marginLeft: 4, padding: '2px 8px', borderRadius: 20,
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                          background: '#fef2f2', color: '#b91c1c',
                        }} title="Media creada directamente como anuncio (no es un post orgánico boosteado)">Patrocinado</span>
                      )}
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {p.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(p.posted_at)}</td>
                <td className="rg2-num">{fmtN(p.like_count)}</td>
                <td className="rg2-num">{fmtN(p.comments_count)}</td>
                <td className="rg2-num">{fmtN(p.reach)}</td>
                <td className="rg2-num">
                  {fmtN(p.views_count)}
                  {p.total_views_count != null && p.total_views_count > p.views_count && (
                    <div style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }} title="Vistas totales incluyendo contenido patrocinado/pauta (Meta total_views_count)">
                      +pauta: {fmtN(p.total_views_count)}
                    </div>
                  )}
                </td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(p.engagement_rate)}</td>
                <td className="rg2-num" style={{ color: '#00c9a7', fontWeight: 700 }}>
                  {p.view_rate != null ? fmtPct(p.view_rate) : <span style={{ color: '#cdd5d3' }}>—</span>}
                </td>
                <td>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Ver ↗</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </> /* end section === 'posts' */}
    </div>
  );
}

/* ── Consolidado: suma métricas de Instagram (META + Mi Marca) y TikTok
   (cuenta OAuth + Mi Marca) del período, para tener un solo número por
   métrica sin importar la plataforma. ── */
function ConsolidadoReport({ metaAccount, fbPagina, tiktokOauthAccount, tiktokTrackedAccountId, marcaProfile, since, until }) {
  const [igPosts, setIgPosts]   = useState([]);
  const [fbPosts, setFbPosts]   = useState([]);
  const [ttPosts, setTtPosts]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loaded, setLoaded]     = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoaded(false);

    const loadIg = async () => {
      try {
        const r = await contentService.getIgDbData(500, since, until, metaAccount?.ig_user_id || '');
        const metaPosts = r.data.posts || [];
        const metaShortcodes = new Set(metaPosts.map(p => extractShortcode(p.permalink)).filter(Boolean));
        try {
          const rm = await contentService.getMarcaPosts({ since, until, limit: 500 });
          const extras = (rm.data.posts || [])
            .filter(p => p.shortcode && !metaShortcodes.has(p.shortcode))
            .map(p => ({
              // Mi Marca (scraper) no trae datos de pauta, así que orgánico = total
              like_count:     p.like_count || 0,
              comments_count: p.comments_count || 0,
              views_count:    p.views_count || 0,
              reach: 0, shares: 0, saved: 0,
            }));
          return [...metaPosts, ...extras];
        } catch {
          return metaPosts;
        }
      } catch {
        // Sin snapshots META: intentamos igual con Mi Marca
        try {
          const rm = await contentService.getMarcaPosts({ since, until, limit: 500 });
          return (rm.data.posts || []).map(p => ({
            like_count: p.like_count || 0, comments_count: p.comments_count || 0,
            views_count: p.views_count || 0, reach: 0, shares: 0, saved: 0,
          }));
        } catch {
          return [];
        }
      }
    };

    const loadTt = async () => {
      let base = [];
      if (tiktokTrackedAccountId) {
        try {
          const days = Math.max(1, Math.ceil((new Date(until) - new Date(since)) / 86400000) + 1);
          const res = await contentService.getTikTokAccountPosts(tiktokTrackedAccountId, days);
          const byVideo = new Map();
          for (const p of (res.data.posts || [])) {
            const prev = byVideo.get(p.video_id);
            if (!prev || new Date(p.snapshot_timestamp) > new Date(prev.snapshot_timestamp)) byVideo.set(p.video_id, p);
          }
          const sinceD = since ? new Date(since) : null;
          const untilD = until ? new Date(until) : null;
          base = Array.from(byVideo.values())
            .filter(p => {
              const d = p.created_time ? new Date(p.created_time) : null;
              if (!d) return true;
              if (sinceD && d < sinceD) return false;
              if (untilD && d > untilD) return false;
              return true;
            })
            .map(p => ({
              id: p.video_id,
              like_count: p.like_count || 0, comments_count: p.comment_count || 0,
              share_count: p.share_count || 0, views_count: p.view_count || 0, saves_count: p.save_count || 0,
            }));
        } catch { /* sin datos OAuth, seguimos con Mi Marca */ }
      }
      if (marcaProfile?.tiktok_username) {
        try {
          const rm = await contentService.getMercadoTikTokPosts({ profile_id: marcaProfile.id, since, until, limit: 500 });
          const knownIds = new Set(base.map(v => String(v.id)));
          const extras = (rm.data.posts || [])
            .filter(p => !knownIds.has(String(p.video_id)))
            .map(p => ({
              like_count: p.like_count || 0, comments_count: p.comments_count || 0,
              share_count: p.share_count || 0, views_count: p.views_count || 0, saves_count: p.saves_count || 0,
            }));
          base = [...base, ...extras];
        } catch { /* sin Mi Marca, seguimos con lo que haya */ }
      }
      return base;
    };

    const loadFb = async () => {
      if (!fbPagina) return [];
      try {
        const r = await contentService.getFbDbData(since, until, fbPagina.page_id);
        return r.data.posts || [];
      } catch {
        return [];
      }
    };

    Promise.all([loadIg(), loadTt(), loadFb()])
      .then(([ig, tt, fb]) => { setIgPosts(ig); setTtPosts(tt); setFbPosts(fb); })
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [metaAccount, fbPagina, tiktokOauthAccount, tiktokTrackedAccountId, marcaProfile, since, until]);

  const kpis = useMemo(() => {
    // Con pauta (orgánico + ads) cuando Meta lo entregó; si no, cae a orgánico.
    // Los posts de Mi Marca (scraper) no tienen total_*, así que quedan igual.
    const igLikes    = igPosts.reduce((s, p) => s + (p.total_like_count || p.like_count || 0), 0);
    const igComments = igPosts.reduce((s, p) => s + (p.total_comments_count || p.comments_count || 0), 0);
    const igViews    = igPosts.reduce((s, p) => s + (p.total_views_count || p.views_count || 0), 0);
    const igReach    = igPosts.reduce((s, p) => s + (p.reach || 0), 0);
    const igShares   = igPosts.reduce((s, p) => s + (p.shares || 0), 0);
    const igSaved    = igPosts.reduce((s, p) => s + (p.saved || 0), 0);

    const ttLikes    = ttPosts.reduce((s, p) => s + (p.like_count || 0), 0);
    const ttComments = ttPosts.reduce((s, p) => s + (p.comments_count || 0), 0);
    const ttViews     = ttPosts.reduce((s, p) => s + (p.views_count || 0), 0);
    const ttShares    = ttPosts.reduce((s, p) => s + (p.share_count || 0), 0);
    const ttSaved     = ttPosts.reduce((s, p) => s + (p.saves_count || 0), 0);

    const fbLikes    = fbPosts.reduce((s, p) => s + (p.likes || 0), 0);
    const fbComments = fbPosts.reduce((s, p) => s + (p.comments || 0), 0);
    const fbShares    = fbPosts.reduce((s, p) => s + (p.shares || 0), 0);
    const fbReach     = fbPosts.reduce((s, p) => s + (p.post_impressions_unique || 0), 0);

    return {
      igCount: igPosts.length, ttCount: ttPosts.length, fbCount: fbPosts.length,
      igLikes, igComments, igViews, igReach, igShares, igSaved,
      ttLikes, ttComments, ttViews, ttShares, ttSaved,
      fbLikes, fbComments, fbShares, fbReach,
      totLikes:    igLikes + ttLikes + fbLikes,
      totComments: igComments + ttComments + fbComments,
      totViews:    igViews + ttViews,
      totShares:   igShares + ttShares + fbShares,
      totSaved:    igSaved + ttSaved,
      totReach:    igReach + fbReach,
      totContent:  igPosts.length + ttPosts.length + fbPosts.length,
    };
  }, [igPosts, ttPosts, fbPosts]);

  if (loading) return <Loading fullScreen={false} mensaje="Consolidando métricas de Instagram, Facebook y TikTok" />;
  if (!loaded) return null;
  if (!kpis.totContent) return <div className="rg2-empty">No hay contenido de Instagram, Facebook ni TikTok en este periodo.</div>;

  return (
    <div className="rg2-report">
      <div style={{
        marginBottom: 14, padding: '8px 14px', borderRadius: 8,
        background: '#f0fdfa', border: '1px solid #99f6e4',
        fontSize: 12, color: '#0f766e', fontWeight: 600,
      }}>
        Suma de Instagram (META + Mi Marca, incl. pauta), Facebook (Página conectada) y TikTok (cuenta conectada + Mi Marca) para el período seleccionado.
        Vistas solo aplica a Instagram + TikTok; Alcance suma Instagram + Facebook (TikTok no expone esa métrica).
      </div>

      {/* KPIs consolidados */}
      <div className="rg2-kpis">
        <KpiCard label="Contenidos"  value={kpis.totContent}
          sub={`${kpis.igCount} IG · ${kpis.fbCount} FB · ${kpis.ttCount} TikTok`} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c"
          sub={`${fmtN(kpis.igLikes)} IG · ${fmtN(kpis.fbLikes)} FB · ${fmtN(kpis.ttLikes)} TikTok`} />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1"
          sub={`${fmtN(kpis.igComments)} IG · ${fmtN(kpis.fbComments)} FB · ${fmtN(kpis.ttComments)} TikTok`} />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2"
          sub={`${fmtN(kpis.igViews)} IG · ${fmtN(kpis.ttViews)} TikTok`} />
        <KpiCard label="Alcance"     value={fmtN(kpis.totReach)}    color="#8b5cf6"
          sub={`${fmtN(kpis.igReach)} IG · ${fmtN(kpis.fbReach)} FB`} />
        <KpiCard label="Guardados"   value={fmtN(kpis.totSaved)}    color="#f59e0b"
          sub={`${fmtN(kpis.igSaved)} IG · ${fmtN(kpis.ttSaved)} TikTok`} />
        <KpiCard label="Compartidos" value={fmtN(kpis.totShares)}   color="#14b8a6"
          sub={`${fmtN(kpis.igShares)} IG · ${fmtN(kpis.fbShares)} FB · ${fmtN(kpis.ttShares)} TikTok`} />
      </div>

      {/* Desglose por plataforma */}
      <div className="rg2-charts-grid" style={{ marginTop: 16 }}>
        <div className="rg2-chart-card">
          <div className="rg2-chart-header"><span className="rg2-chart-title">Instagram</span></div>
          <div style={{ padding: '4px 14px 14px' }}>
            {[
              { label: 'Posts', val: kpis.igCount },
              { label: 'Likes (con pauta)', val: fmtN(kpis.igLikes) },
              { label: 'Comentarios (con pauta)', val: fmtN(kpis.igComments) },
              { label: 'Vistas (con pauta)', val: fmtN(kpis.igViews) },
              { label: 'Alcance', val: fmtN(kpis.igReach) },
              { label: 'Guardados', val: fmtN(kpis.igSaved) },
              { label: 'Compartidos', val: fmtN(kpis.igShares) },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#475569' }}>
                <span>{r.label}</span><strong style={{ color: '#002421' }}>{r.val}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="rg2-chart-card">
          <div className="rg2-chart-header"><span className="rg2-chart-title">Facebook</span></div>
          <div style={{ padding: '4px 14px 14px' }}>
            {[
              { label: 'Posts', val: kpis.fbCount },
              { label: 'Likes', val: fmtN(kpis.fbLikes) },
              { label: 'Comentarios', val: fmtN(kpis.fbComments) },
              { label: 'Alcance', val: fmtN(kpis.fbReach) },
              { label: 'Compartidos', val: fmtN(kpis.fbShares) },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#475569' }}>
                <span>{r.label}</span><strong style={{ color: '#002421' }}>{r.val}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="rg2-chart-card">
          <div className="rg2-chart-header"><span className="rg2-chart-title">TikTok</span></div>
          <div style={{ padding: '4px 14px 14px' }}>
            {[
              { label: 'Videos', val: kpis.ttCount },
              { label: 'Likes', val: fmtN(kpis.ttLikes) },
              { label: 'Comentarios', val: fmtN(kpis.ttComments) },
              { label: 'Vistas', val: fmtN(kpis.ttViews) },
              { label: 'Guardados', val: fmtN(kpis.ttSaved) },
              { label: 'Compartidos', val: fmtN(kpis.ttShares) },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#475569' }}>
                <span>{r.label}</span><strong style={{ color: '#002421' }}>{r.val}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ── */
export default function ReporteGeves() {
  const [since, setSince] = useState(iso30DaysAgo);
  const [until, setUntil] = useState(isoToday);
  const [appliedSince, setAppliedSince] = useState(iso30DaysAgo);
  const [appliedUntil, setAppliedUntil] = useState(isoToday);

  const [mercado, setMercado]   = useState(null);
  const [loadingMercado, setLoadingMercado] = useState(true);
  const [activeTab, setActiveTab] = useState(null); // profile id, null = marca, 'meta-XXX' = cuenta meta
  const [platform, setPlatform] = useState('instagram'); // 'instagram' | 'tiktok'
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [fbPages, setFbPages] = useState([]);
  const [tiktokOauthAccounts, setTiktokOauthAccounts]     = useState([]);
  const [tiktokTrackedAccounts, setTiktokTrackedAccounts] = useState([]);

  // ── Exportar PPT ──────────────────────────────────────────────────────────
  const [pptModal, setPptModal]         = useState(false);
  const [pptTemplates, setPptTemplates] = useState([]);
  const [pptSelId, setPptSelId]         = useState('');
  const [pptLoading, setPptLoading]     = useState(false);
  const [pptError, setPptError]         = useState('');

  const openPptModal = () => {
    setPptError(''); setPptSelId('');
    contentService.getPPTTemplates()
      .then(r => { setPptTemplates(r.data.templates || []); setPptModal(true); })
      .catch(() => { setPptTemplates([]); setPptModal(true); });
  };

  const handleGeneratePPT = async () => {
    if (!pptSelId) { setPptError('Selecciona una plantilla.'); return; }
    // Obtener ig_user_id de la tab activa Meta
    const igUserId = String(activeTab || '').startsWith('meta-')
      ? String(activeTab).replace('meta-', '')
      : (metaAccounts[0]?.ig_user_id || '');
    setPptLoading(true); setPptError('');
    try {
      const res = await contentService.generatePPT(pptSelId, igUserId, appliedSince, appliedUntil);
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_${igUserId || 'cuenta'}_${appliedSince}_${appliedUntil}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      setPptModal(false);
    } catch (err) {
      setPptError('Error al generar el PPT. Intenta nuevamente.');
    } finally {
      setPptLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPlatform('instagram');
  };

  useEffect(() => {
    contentService.getMercado()
      .then(r => {
        setMercado(r.data);
        setActiveTab(null);
      })
      .catch(() => setMercado(null))
      .finally(() => setLoadingMercado(false));

    contentService.getIgAccounts()
      .then(r => setMetaAccounts(r.data.accounts || []))
      .catch(() => setMetaAccounts([]));

    contentService.getFbPages()
      .then(r => setFbPages(r.data.pages || []))
      .catch(() => setFbPages([]));

    Promise.all([
      contentService.getMyTikTokAccount().catch(() => ({ data: null })),
      contentService.getTrackedTikTokAccounts().catch(() => ({ data: [] })),
    ]).then(([{ data: my }, { data: tracked }]) => {
      let accounts = [];
      if (my && Array.isArray(my.accounts)) accounts = my.accounts;
      else if (my && my.id) accounts = [my];
      setTiktokOauthAccounts(accounts);
      setTiktokTrackedAccounts(Array.isArray(tracked) ? tracked : (tracked.results || []));
    });
  }, []);

  const handleBuscar = () => {
    setAppliedSince(since);
    setAppliedUntil(until);
  };

  // Construir lista de tabs
  const tabs = useMemo(() => {
    if (!mercado) return [];
    const list = [];
    if (mercado.marca) {
      list.push({ ...mercado.marca, role: 'Mi Marca', is_marca: true, tabId: null });
    }
    (mercado.competencia || []).forEach(p => list.push({ ...p, role: 'Competencia', tabId: p.id }));
    (mercado.referencia  || []).forEach(p => list.push({ ...p, role: 'Referencia',  tabId: p.id }));
    return list;
  }, [mercado]);

  const activeProfile = useMemo(() => {
    if (!tabs.length) return null;
    return tabs.find(t => t.tabId === activeTab) || tabs[0];
  }, [tabs, activeTab]);

  if (loadingMercado) {
    return <div className="rg2-page"><Loading fullScreen={false} mensaje="Cargando mercado" /></div>;
  }

  if (!mercado?.marca && !tabs.length) {
    return (
      <div className="rg2-page">
        <div className="rg2-empty-state">
          <div style={{ fontSize: 40 }}>📊</div>
          <p style={{ fontWeight: 600, color: '#475569', marginTop: 12 }}>No hay una marca configurada</p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>
            Ve a <a href="/mercado" style={{ color: '#6366f1' }}>Mercado</a> y configura tu marca, competencia o referencia.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rg2-page">
      {/* Modal Exportar PPT */}
      {pptModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setPptModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, padding: 32, width: 420, maxWidth: '92vw',
            boxShadow: '0 20px 60px rgba(0,36,33,0.18)',
          }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#002421' }}>
              Exportar PPT
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
              Selecciona una plantilla para rellenar con los datos del período <strong>{appliedSince}</strong> → <strong>{appliedUntil}</strong>.
            </p>

            {!pptTemplates.length ? (
              <div style={{ padding: '16px 0', color: '#94a3b8', fontSize: 13 }}>
                No tienes plantillas guardadas.{' '}
                <a href="/configuracion/plantillas-ppt" style={{ color: '#6366f1' }}>
                  Crear una →
                </a>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {pptTemplates.map(t => (
                  <label key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    border: `2px solid ${pptSelId === String(t.id) ? '#002421' : '#e2e8f0'}`,
                    borderRadius: 10, cursor: 'pointer',
                    background: pptSelId === String(t.id) ? '#f0fdf4' : '#fff',
                    transition: 'all .15s',
                  }}>
                    <input type="radio" name="ppt-tmpl" value={t.id}
                      checked={pptSelId === String(t.id)}
                      onChange={() => setPptSelId(String(t.id))}
                      style={{ accentColor: '#002421' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#002421' }}>{t.nombre}</div>
                      {t.descripcion && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>{t.descripcion}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {pptError && (
              <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{pptError}</div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setPptModal(false)} style={{
                padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Cancelar</button>
              <button onClick={handleGeneratePPT} disabled={pptLoading || !pptTemplates.length} style={{
                padding: '9px 20px', borderRadius: 8, border: 'none',
                background: '#002421', color: '#fff', fontWeight: 700, fontSize: 13,
                cursor: pptLoading ? 'not-allowed' : 'pointer',
                opacity: pptLoading ? 0.6 : 1, fontFamily: 'inherit',
              }}>
                {pptLoading ? 'Generando…' : '⬇ Descargar PPT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rg2-header">
        <h1 className="rg2-title">Reporte Geves</h1>
        <div className="rg2-date-row">
          <label className="rg2-date-label">Desde</label>
          <input type="date" className="rg2-date-input" value={since}
            onChange={e => setSince(e.target.value)} max={until} />
          <label className="rg2-date-label">Hasta</label>
          <input type="date" className="rg2-date-input" value={until}
            onChange={e => setUntil(e.target.value)} min={since} max={isoToday()} />
          <button className="rg2-btn-buscar" onClick={handleBuscar}>Buscar</button>
          <button onClick={openPptModal} style={{
            padding: '7px 16px', borderRadius: 8,
            border: '1.5px solid rgba(0,36,33,0.2)',
            background: '#fff', color: '#002421', fontWeight: 700,
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            📊 Exportar PPT
          </button>
        </div>
      </div>

      {/* Tabs de perfiles */}
      <div className="rg2-profile-tabs">
        {tabs.map(t => (
          <button
            key={t.tabId ?? 'marca'}
            className={`rg2-profile-tab${activeTab === t.tabId ? ' active' : ''}`}
            onClick={() => handleTabChange(t.tabId)}
          >
            <Avatar profile={t} size={28} />
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                @{t.username}
              </div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>{t.role}</div>
            </div>
          </button>
        ))}
        {/* Cuentas Meta conectadas */}
        {metaAccounts.map(acc => {
          const tabId = `meta-${acc.ig_user_id}`;
          return (
            <button
              key={tabId}
              className={`rg2-profile-tab${activeTab === tabId ? ' active' : ''}`}
              onClick={() => handleTabChange(tabId)}
            >
              <Avatar profile={{ username: acc.username, profile_pic_url: acc.profile_pic_url }} size={28} />
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  @{acc.username}
                </div>
                <div style={{ fontSize: 10, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ background: '#e1306c', borderRadius: 3, color: '#fff', padding: '0 4px', fontSize: 9, fontWeight: 800, letterSpacing: '0.04em' }}>META</span>
                  Mi cuenta
                </div>
              </div>
            </button>
          );
        })}
        {/* Páginas Facebook conectadas */}
        {fbPages.map(pagina => {
          const tabId = `metafb-${pagina.page_id}`;
          return (
            <button
              key={tabId}
              className={`rg2-profile-tab${activeTab === tabId ? ' active' : ''}`}
              onClick={() => handleTabChange(tabId)}
            >
              <Avatar profile={{ username: pagina.nombre, profile_pic_url: pagina.picture_url }} size={28} />
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pagina.nombre}
                </div>
                <div style={{ fontSize: 10, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ background: '#1877f2', borderRadius: 3, color: '#fff', padding: '0 4px', fontSize: 9, fontWeight: 800, letterSpacing: '0.04em' }}>FACEBOOK</span>
                  Mi cuenta
                </div>
              </div>
            </button>
          );
        })}
        {/* Cuentas TikTok conectadas (OAuth) */}
        {tiktokOauthAccounts.map(acc => {
          const tabId = `tiktokown-${acc.id}`;
          return (
            <button
              key={tabId}
              className={`rg2-profile-tab${activeTab === tabId ? ' active' : ''}`}
              onClick={() => handleTabChange(tabId)}
            >
              <Avatar profile={{ username: acc.username, profile_pic_url: acc.avatar_url }} size={28} />
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  @{acc.username}
                </div>
                <div style={{ fontSize: 10, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ background: '#000', borderRadius: 3, color: '#fff', padding: '0 4px', fontSize: 9, fontWeight: 800, letterSpacing: '0.04em' }}>TIKTOK</span>
                  Mi cuenta
                </div>
              </div>
            </button>
          );
        })}
        {/* Reporte Marca: META + collabs de "Mi Marca" no capturados por META */}
        {mercado?.marca && metaAccounts.length > 0 && (
          <button
            key="reporte-marca"
            className={`rg2-profile-tab${activeTab === 'reporte-marca' ? ' active' : ''}`}
            onClick={() => handleTabChange('reporte-marca')}
          >
            <Avatar profile={mercado.marca} size={28} />
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                @{mercado.marca.username}
              </div>
              <div style={{ fontSize: 10, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ background: '#9a3412', borderRadius: 3, color: '#fff', padding: '0 4px', fontSize: 9, fontWeight: 800, letterSpacing: '0.04em' }}>MARCA</span>
                Reporte Marca
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Platform switcher para Reporte Marca: Instagram, Facebook, TikTok, Consolidado */}
      {activeTab === 'reporte-marca' && (mercado?.marca?.tiktok_username || fbPages.length > 0) && (
        <div className="rg2-platform-switcher" style={{ display: 'flex', gap: 8, padding: '0 0 16px 0', marginBottom: 4, flexWrap: 'wrap' }}>
          <button
            onClick={() => setPlatform('instagram')}
            style={{
              padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.01em',
              border: platform === 'instagram' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
              background: platform === 'instagram' ? '#002421' : '#fff',
              color: platform === 'instagram' ? '#fff' : '#4a5854',
            }}
          >
            Instagram
          </button>
          {fbPages.length > 0 && (
            <button
              onClick={() => setPlatform('facebook')}
              style={{
                padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.01em',
                border: platform === 'facebook' ? '2px solid #1877f2' : '1.5px solid rgba(24,119,242,0.25)',
                background: platform === 'facebook' ? '#1877f2' : '#fff',
                color: platform === 'facebook' ? '#fff' : '#1877f2',
              }}
            >
              Facebook
            </button>
          )}
          {mercado?.marca?.tiktok_username && (
            <button
              onClick={() => setPlatform('tiktok')}
              style={{
                padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.01em',
                border: platform === 'tiktok' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
                background: platform === 'tiktok' ? '#002421' : '#fff',
                color: platform === 'tiktok' ? '#fff' : '#4a5854',
              }}
            >
              TikTok @{mercado.marca.tiktok_username}
            </button>
          )}
          <button
            onClick={() => setPlatform('consolidado')}
            style={{
              padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.01em',
              border: platform === 'consolidado' ? '2px solid #0f766e' : '1.5px solid rgba(15,118,110,0.25)',
              background: platform === 'consolidado' ? '#0f766e' : '#fff',
              color: platform === 'consolidado' ? '#fff' : '#0f766e',
            }}
          >
            Consolidado
          </button>
        </div>
      )}

      {/* Platform switcher (solo si el perfil tiene TikTok y no es tab Meta/Facebook/TikTok propios ni Reporte Marca) */}
      {activeProfile?.tiktok_username && activeTab !== 'reporte-marca'
        && !String(activeTab || '').startsWith('meta-')
        && !String(activeTab || '').startsWith('metafb-')
        && !String(activeTab || '').startsWith('tiktokown-') && (
        <div className="rg2-platform-switcher" style={{ display: 'flex', gap: 8, padding: '0 0 16px 0', marginBottom: 4 }}>
          <button
            onClick={() => setPlatform('instagram')}
            style={{
              padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.01em',
              border: platform === 'instagram' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
              background: platform === 'instagram' ? '#002421' : '#fff',
              color: platform === 'instagram' ? '#fff' : '#4a5854',
            }}
          >
            Instagram
          </button>
          <button
            onClick={() => setPlatform('tiktok')}
            style={{
              padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.01em',
              border: platform === 'tiktok' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
              background: platform === 'tiktok' ? '#002421' : '#fff',
              color: platform === 'tiktok' ? '#fff' : '#4a5854',
            }}
          >
            TikTok @{activeProfile.tiktok_username}
          </button>
        </div>
      )}

      {/* Reporte Meta (cuentas propias desde DB) */}
      {activeTab === 'reporte-marca' ? (() => {
        if (platform === 'tiktok' && mercado?.marca?.tiktok_username) {
          // Cuenta TikTok OAuth conectada (si existe) + posts de "Mi Marca" que no capturó
          const oauthAcc = tiktokOauthAccounts[0];
          const tracked  = oauthAcc ? tiktokTrackedAccounts.find(t => t.username === oauthAcc.username) : null;
          return (
            <TikTokOwnReport
              key={`reporte-marca-tt-${oauthAcc?.id || 'none'}-${appliedSince}-${appliedUntil}`}
              oauthAccount={oauthAcc}
              trackedAccountId={tracked?.id}
              since={appliedSince}
              until={appliedUntil}
              marcaProfile={mercado.marca}
              mergeMarca
            />
          );
        }
        if (platform === 'facebook' && fbPages.length > 0) {
          return (
            <FacebookDbReport
              key={`reporte-marca-fb-${fbPages[0].page_id}-${appliedSince}-${appliedUntil}`}
              pagina={fbPages[0]}
              since={appliedSince}
              until={appliedUntil}
            />
          );
        }
        if (platform === 'consolidado') {
          const oauthAcc = tiktokOauthAccounts[0];
          const tracked  = oauthAcc ? tiktokTrackedAccounts.find(t => t.username === oauthAcc.username) : null;
          return (
            <ConsolidadoReport
              key={`reporte-marca-consolidado-${appliedSince}-${appliedUntil}`}
              metaAccount={metaAccounts[0]}
              fbPagina={fbPages[0]}
              tiktokOauthAccount={oauthAcc}
              tiktokTrackedAccountId={tracked?.id}
              marcaProfile={mercado.marca}
              since={appliedSince}
              until={appliedUntil}
            />
          );
        }
        // Usa la primera cuenta Meta conectada (normalmente hay una sola, la de la marca)
        const acc = metaAccounts[0];
        if (!acc) return null;
        return (
          <MetaDbReport
            key={`reporte-marca-${acc.ig_user_id}-${appliedSince}-${appliedUntil}`}
            account={acc}
            since={appliedSince}
            until={appliedUntil}
            mergeMarca
          />
        );
      })() : activeTab && String(activeTab).startsWith('meta-') ? (() => {
        const igUserId = String(activeTab).replace('meta-', '');
        const acc = metaAccounts.find(a => a.ig_user_id === igUserId);
        if (!acc) return null;
        return (
          <MetaDbReport
            key={`meta-${igUserId}-${appliedSince}-${appliedUntil}`}
            account={acc}
            since={appliedSince}
            until={appliedUntil}
          />
        );
      })() : activeTab && String(activeTab).startsWith('metafb-') ? (() => {
        const pageId = String(activeTab).replace('metafb-', '');
        const pagina = fbPages.find(p => p.page_id === pageId);
        if (!pagina) return null;
        return (
          <FacebookDbReport
            key={`metafb-${pageId}-${appliedSince}-${appliedUntil}`}
            pagina={pagina}
            since={appliedSince}
            until={appliedUntil}
          />
        );
      })() : activeTab && String(activeTab).startsWith('tiktokown-') ? (() => {
        const oauthId = String(activeTab).replace('tiktokown-', '');
        const acc = tiktokOauthAccounts.find(a => String(a.id) === oauthId);
        if (!acc) return null;
        const tracked = tiktokTrackedAccounts.find(t => t.username === acc.username);
        return (
          <TikTokOwnReport
            key={`tiktokown-${oauthId}-${appliedSince}-${appliedUntil}`}
            oauthAccount={acc}
            trackedAccountId={tracked?.id}
            since={appliedSince}
            until={appliedUntil}
          />
        );
      })() : (
        <>
          {/* Evolución de seguidores del perfil activo */}
          {activeProfile && (
            <FollowersChart
              key={`fc-${activeProfile.id}-${appliedSince}-${appliedUntil}`}
              since={appliedSince}
              until={appliedUntil}
              profileId={activeProfile.id}
            />
          )}

          {/* Reporte del perfil activo */}
          {activeProfile && platform === 'instagram' && (
            <ProfileReport
              key={`ig-${activeProfile.id}-${appliedSince}-${appliedUntil}`}
              profile={activeProfile}
              since={appliedSince}
              until={appliedUntil}
            />
          )}
          {activeProfile?.tiktok_username && platform === 'tiktok' && (
            <TikTokReport
              key={`tt-${activeProfile.id}-${appliedSince}-${appliedUntil}`}
              profile={activeProfile}
              since={appliedSince}
              until={appliedUntil}
            />
          )}
        </>
      )}
    </div>
  );
}
